import { CloseRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "common/customIcons/search";
import MDButton from "components/MDButton";
import { AgGridTable } from "components/tables/AgGridTable";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CommonLayout from "layouts/DashboardLayout";
import { Link, useNavigate } from "react-router-dom";
import React, { useCallback, useEffect, useRef, useState } from "react";
import EditIcon from "common/customIcons/edit";
import DeleteIcon from "common/customIcons/delete";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { deleteMainTrait, getTraitListing } from "../../redux/slices/traitSlice";
import { formatTimestampToDate } from "utilities/common";
import FilterTraits from "common/CommonDrawer/traits/FilterTraits";
import {
  closeCustomModalDialog,
  openCustomModalDialog,
  setModalLoader,
} from "../../redux/slices/modalSlice";
import { openSnackbar } from "../../redux/slices/common";
import ProductSubTraits from "./subTraits";
import { useDebounce } from "hooks/useDebounce";
import { getPermissionsByMode } from "utilities/common";
import { hasSectionPermission } from "utilities/common";
import { getData } from "utilities/common";

export default function ProductTraits() {
  const { traits = {}, loading = false } = useSelector((state) => state.traitSlice);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const searchTermRef = useRef("");
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [currentTraitsTab, setCurrentTraitsTab] = useState("mainTraits");
  const traitFiltersPayload = useRef({});
  const mainGridApiRef = useRef("");
  const pageRef = useRef(0);
  const gridRefApi = useRef(null);


  let localUser = getData('login-user-info')
  if (localUser) {
    localUser = JSON.parse(localUser)
  }

  const adminMode = localStorage.getItem("adminsMode") ? JSON.parse(localStorage.getItem("adminsMode")) : null;
  const permissionsForMode = getPermissionsByMode(localUser?.permissions, adminMode);

  const canCreate = hasSectionPermission(permissionsForMode, "Product", "Create");
  const canEdit = hasSectionPermission(permissionsForMode, "Product", "Edit/Update");
  const canView = hasSectionPermission(permissionsForMode, "Product", "View Only");
  const canDelete = hasSectionPermission(permissionsForMode, "Product", "Delete");

  const columnDefs = [
    {
      headerName: "Name",
      field: "mainTraitName",
      suppressMenu: false,
      minWidth: 140,
    },
    {
      headerName: "Platform",
      sortable: false,
      field: "platformAvailability",
      suppressMenu: false,
      minWidth: 100,
      flex: 1,
      cellStyle: { whiteSpace: "normal", lineHeight: "1.5" },
      cellRenderer: (props) => {
        const platformData = props?.value;

        if (!platformData) return "-";

        // If "all" is true, return "All"
        if (platformData.all)
          return (
            <span
              className="tab"

            // className="platform-badge platform-badge-all"
            >
              All
            </span>
          );

        // Filter out keys with `true` values and render them as separate elements
        const activePlatforms = Object.keys(platformData)
          .filter((key) => platformData[key] && key !== "all")
          .map((platform, index) => (
            <span
              key={index}
              className="tab"
            // className="platform-badge"
            >
              {platform}
            </span>
          ));

        return activePlatforms.length > 0 ? activePlatforms : "-";
      },
    },
    {
      headerName: "Created By",
      field: "createdBy",
      key: "createdBy",
      suppressMenu: false,
      minWidth: 180,
      cellStyle: {
        whiteSpace: "normal",
        lineHeight: 1.5,
      },
    },
    {
      headerName: "Updated By",
      field: "updatedBy",
      key: "updatedBy",
      suppressMenu: false,
      minWidth: 180,
      cellRenderer: (props) => {
        if (!props?.value) {
          return "-";
        } else {
          return props?.value;
        }
      },
      cellStyle: {
        whiteSpace: "normal",
        lineHeight: 1.5,
      },
    },
    {
      headerName: "Updated At",
      field: "updatedAt",
      key: "updatedDateAt",
      suppressMenu: false,
      minWidth: 180,
      cellStyle: {
        whiteSpace: "normal",
        lineHeight: 1.5,
      },
    },
    {
      headerName: "Action",
      suppressMenu: true, 
      cellRenderer: (props) => {
        return (
          <Box display="inline-flex" alignItems="center">
            <IconButton
              disabled={!canEdit ? true : false}
              onClick={(e) => {
                e.stopPropagation();
                onClickEdit(props);
              }}
              color="primary"
              size="small"
              variant="primary"
            >
              <EditIcon width="16px" height="16px" />
            </IconButton>
          </Box>
        );
      },
      cellStyle: { textAlign: "right", justifyContent: "end" },
      headerClass: "right-aligned-cell-header",
      minWidth: 120,
      maxWidth: 100,
      pinned: "right",
    },
  ];

  function getSortInfo(data) {
    const result = {};
    data.forEach((item) => {
      if (item?.colId === "mainTraitName") {
        result[item.colId] = item.sort === "asc" ? "high" : "low";
      } else if (item?.colId === "status") {
        result["statusSort"] = item.sort === "asc" ? "high" : "low";
      } else if (item?.colId === "updatedAt") {
        result[item.colId] = item.sort === "asc" ? "high" : "low";
      }
    });

    return result;
  }

  const getRows = async (params) => {
    gridRefApi.current = params;
    let filter = {};

    if (params?.sortModel.length) {
      filter = { ...getSortInfo(params?.sortModel) };
    }
    dispatch(
      getTraitListing({
        skip: pageRef.current,
        searchTerm: searchTermRef.current,
        limit: 20,
        userType: "admin",
        ...filter,
      })
    )
      .unwrap()
      .then((res) => {
        const { traits, total } = res;
        const traitsArray = traits?.map((item) => {
          return { ...item, id: item._id, updatedAt: formatTimestampToDate(item?.updatedDate) };
        });
        params.successCallback([...traitsArray], total);
        pageRef.current = pageRef.current + 20;
      });
  };

  const searchFn = () => {
    const dataSource = {
      getRows,
    };
    if (mainGridApiRef.current) {
      mainGridApiRef.current.ensureIndexVisible(0, null);
      mainGridApiRef.current.setGridOption("datasource", dataSource);
    }
  };

  const handleSearch = useDebounce(() => {
    pageRef.current = 0;
    searchFn();
  }, 500);

  const searchHandler = (searchValue) => {
    setSearchTerm(searchValue);
    searchTermRef.current = searchValue;
    handleSearch(searchValue);
  };

  const onRowClicked = (row) => {
    if (row?.colDef?.headerName !== "Action" && row?.colDef?.headerName !== "Product Name/SKU") {
      navigate(`/product_traits/${row?.data?.mainTraitName}`);
      // navigate(`/product_traits/${row?.data?.traitSlug}`);

    }
  };

  const onClickEdit = (props) => {
    // navigate(`/product_traits/${props?.data?.traitSlug}`);
    navigate(`/product_traits/${props?.data?.mainTraitName}`);

  };

  const showError = (message, errortype) => {
    if (errortype === "success") {
      dispatch(openSnackbar({ content: message, color: "success", icon: "check" }));
    }
    if (errortype === "error") {
      dispatch(openSnackbar({ content: message, color: "error", icon: "warning" }));
    }
  };

  const resetData = () => {
    pageRef.current = 0;
    if (mainGridApiRef.current) {
      mainGridApiRef.current.forEachNode((node) => {
        node.setSelected(false);
      });
    }
    searchFn();
    setAnchorEl(null);
  };

  const applyTraitFilter = (filterPayload) => {
    pageRef.current = 0;
    traitFiltersPayload.current = filterPayload;
    const dataSource = {
      getRows: (params) => {
        dispatch(
          getTraitListing({
            skip: pageRef.current,
            searchTerm: searchTermRef.current,
            userType: "admin",
            limit: 20,
            ...filterPayload?.queryPayload,
          })
        )
          .unwrap()
          .then((res) => {
            const { traits, total } = res;
            const traitsArray = traits?.map((item) => {
              return {
                ...item,
                id: item._id,
                updatedDate: formatTimestampToDate(item?.updatedDate),
              };
            });
            params.successCallback(traitsArray, total);
            pageRef.current = pageRef.current + 20;
          });
      },
    };
    if (mainGridApiRef?.current) {
      mainGridApiRef.current.ensureIndexVisible(0, null);
      mainGridApiRef.current.setGridOption("datasource", dataSource);
    }
  };

  useEffect(() => {
    if (mainGridApiRef?.current) {
      if (loading) {
        mainGridApiRef.current.showLoadingOverlay();
      } else if (traits?.total === 0) {
        mainGridApiRef.current.showNoRowsOverlay();
      } else {
        mainGridApiRef.current.hideOverlay();
      }
    }
  }, [traits, loading]);

  const handleTabChange = (tab) => {
    setCurrentTraitsTab(tab);

    if (tab === "mainTraits") {
      navigate("/product_traits"); // Navigate to the Traits URL
    } else if (tab === "subTrait") {
      navigate("/product_traits/subtrait"); // Navigate to the subTraits URL
    }
  };

  return (
    <>
      {
        <>
          {currentTraitsTab === "mainTraits" ? (
            <CommonLayout>
              <Box p={{ xs: 2, sm: 3, lg: 4 }} pb={{ lg: 2 }} className="row-pointer">
                <Box mb={2} className="cannabinoids-tabs">
                  <Stack direction="row" gap={1} sx={{ background: "#ffffff" }}>
                    <Button
                      onClick={() => handleTabChange("mainTraits")}
                      variant="outlined"
                      sx={{ color: "#2E2E3A", minWidth: "180px" }}
                      className={currentTraitsTab === "mainTraits" ? "active" : ""}
                    >
                      Major Traits
                    </Button>
                    <Button
                      className={currentTraitsTab === "subTrait" ? "active" : ""}
                      onClick={() => handleTabChange("subTrait")}
                      variant="outlined"
                      sx={{ color: "#2E2E3A", minWidth: "180px" }}
                    >
                      Sub Traits
                    </Button>
                  </Stack>
                </Box>

                <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={2}>
                  <Grid item xs={12} sm={2}>
                    <Typography variant="h4">Major Traits</Typography>
                  </Grid>

                  <Grid item xs={12} sm={10}>
                    <Stack
                      direction="row"
                      justifyContent={{ xs: "start", sm: "end" }}
                      flexWrap="wrap"
                      alignItems="stretch"
                      gap={2}
                    >
                      {/* <Search  placeholder='Search' label='' value={searchTerm} onChange={searchHandler} /> */}

                      <TextField
                        className="custom-search-field"
                        placeholder="Search"
                        label=""
                        value={searchTerm}
                        onChange={(e) => searchHandler(e.target.value)}
                        sx={{ minWidth: "280px" }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <IconButton size="small">
                                <SearchIcon width="18px" height="18px" />
                              </IconButton>
                            </InputAdornment>
                          ),
                          endAdornment:
                            searchTerm?.length > 0 ? (
                              <InputAdornment>
                                <IconButton onClick={() => searchHandler("")} size="small">
                                  <CloseRounded />
                                </IconButton>
                              </InputAdornment>
                            ) : null,
                        }}
                      />
                      {
                        canCreate &&
                        <Link to="/product_traits/add">
                          <MDButton
                            style={{ height: "100%", minWidth: "140px" }}
                            startIcon={<AddRoundedIcon fontSize="large" />}
                            variant="contained"
                            color="primary"
                          >
                            New
                          </MDButton>
                        </Link>
                      }
                    </Stack>
                  </Grid>
                </Grid>
                <FilterTraits applyTraitFilter={applyTraitFilter} />
                {canView &&
                  <AgGridTable
                    col={columnDefs}
                    mainGridApiRef={mainGridApiRef}
                    getRows={getRows}
                    pageRef={pageRef}
                    isLoading={loading}
                    onRowClicked={onRowClicked}
                    style={{ height: "56vh" }}
                    gridOptions={{
                      rowHeight: 64,
                    }}
                  />
                }
              </Box>
            </CommonLayout>
          ) : (
            <ProductSubTraits />
          )}
        </>
      }
    </>
  );
}
